import React, { useState, useEffect, useCallback } from "react"
import PropTypes from "prop-types"
import { connect } from "react-redux"
import { useNavigate } from "react-router-dom"
import { Spinner } from "reactstrap"
import { setBreadcrumbItems } from "../../../store/actions"
import { crearPresupuesto, listarSucursalesEmpresa } from "../api"
import {
  PERIODICIDADES, TIPOS_MEDICION, monedaList, ANIOS_DISPONIBLES,
  toDateInput,
} from "../helpers"

const breadcrumbItems = [
  { title: "LS-DA", link: "#" },
  { title: "Presupuestos", link: "#" },
  { title: "Nuevo presupuesto", link: "#" },
]

const ahora = new Date()
const anioActual = ahora.getFullYear()

const CrearPresupuesto = ({ empresaSeleccionada, user, setBreadcrumbItems: setBreadcrumb }) => {
  const navigate = useNavigate()

  const [sucursales, setSucursales] = useState([])
  const [loadingSucursales, setLoadingSucursales] = useState(false)

  const [form, setForm] = useState({
    sucursalId: "",
    nombre: "",
    descripcion: "",
    anio: anioActual,
    periodicidad: "MENSUAL",
    fechaInicio: `${anioActual}-01-01`,
    fechaFin: `${anioActual}-12-31`,
    tipoMedicion: "MONTO",
    moneda: "COP",
    montoTotal: "",
    unidadesTotal: "",
    notas: "",
  })

  const [enviando, setEnviando] = useState(false)
  const [error, setError] = useState("")
  const [validacion, setValidacion] = useState([])

  const empresaId = empresaSeleccionada?.id || empresaSeleccionada?.idempresa || ""
  const empresaNombre = empresaSeleccionada?.nombre || ""

  useEffect(() => {
    document.title = "Crear presupuesto | LS-DA"
    const greeting = user
      ? `Bienvenido, ${user.nombre || ""} ${user.apellido || ""}`.trim()
      : "Crear presupuesto"
    setBreadcrumb(greeting, breadcrumbItems)
  }, [user, setBreadcrumb])

  const cargarSucursales = useCallback(async () => {
    if (!empresaId) return
    setLoadingSucursales(true)
    try {
      const r = await listarSucursalesEmpresa(empresaId)
      const body = r.data
      setSucursales(body?.data || body?.sucursales || [])
    } catch {
      setSucursales([])
    } finally {
      setLoadingSucursales(false)
    }
  }, [empresaId])

  useEffect(() => { cargarSucursales() }, [cargarSucursales])

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }))

  // Sincronizar fechas con año por defecto si el usuario cambia año
  const onAnioChange = (newAnio) => {
    setForm(prev => ({
      ...prev,
      anio: newAnio,
      fechaInicio: `${newAnio}-01-01`,
      fechaFin: `${newAnio}-12-31`,
    }))
  }

  const validar = () => {
    const errs = []
    if (!empresaId) errs.push("No hay empresa seleccionada.")
    if (!form.nombre.trim()) errs.push("El nombre es obligatorio.")
    if (!form.anio) errs.push("El año es obligatorio.")
    if (!form.periodicidad) errs.push("La periodicidad es obligatoria.")
    if (!form.fechaInicio || !form.fechaFin) errs.push("Las fechas son obligatorias.")
    if (form.fechaInicio > form.fechaFin) errs.push("La fecha de inicio no puede ser posterior a la fecha fin.")
    if (form.tipoMedicion === "MONTO" && form.montoTotal && Number(form.montoTotal) < 0) errs.push("El monto no puede ser negativo.")
    if (form.tipoMedicion === "UNIDADES" && form.unidadesTotal && Number(form.unidadesTotal) < 0) errs.push("Las unidades no pueden ser negativas.")
    return errs
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const errs = validar()
    setValidacion(errs)
    if (errs.length > 0) return

    setEnviando(true)
    setError("")
    try {
      const body = {
        empresaId,
        sucursalId: form.sucursalId || null,
        nombre: form.nombre.trim(),
        descripcion: form.descripcion.trim() || null,
        anio: Number(form.anio),
        periodicidad: form.periodicidad,
        fechaInicio: form.fechaInicio,
        fechaFin: form.fechaFin,
        tipoMedicion: form.tipoMedicion,
        moneda: form.moneda,
        montoTotal: form.montoTotal ? Number(form.montoTotal) : 0,
        unidadesTotal: form.unidadesTotal ? Number(form.unidadesTotal) : 0,
        notas: form.notas.trim() || null,
      }
      const r = await crearPresupuesto(body)
      const datos = r.data?.data || r.data
      // Al crear con éxito, vamos al listado (el back ya generó periodos y distribución lineal)
      navigate("/portal/presupuestos", { state: { creadoId: datos?.id } })
    } catch (err) {
      const apiErrors = err.response?.data?.errors
      if (Array.isArray(apiErrors)) {
        setValidacion(apiErrors.map(e => `${e.campo || "Error"}: ${e.mensaje || ""}`))
      }
      setError(err.response?.data?.message || err.message || "No se pudo crear el presupuesto.")
    } finally {
      setEnviando(false)
    }
  }

  if (!empresaId) {
    return (
      <div style={{ background: "#fff", borderRadius: 14, padding: 60, textAlign: "center", border: "1px dashed #cbd5e1" }}>
        <i className="mdi mdi-domain-off" style={{ fontSize: 56, color: "#cbd5e1" }} />
        <h5 className="mt-3" style={{ color: "#0f172a" }}>Selecciona una empresa</h5>
        <p style={{ color: "#64748b", maxWidth: 420, margin: "8px auto 0" }}>
          Usa el selector <strong>“Seleccionar empresa”</strong> arriba a la derecha.
        </p>
      </div>
    )
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ background: "#fff", borderRadius: 14, padding: "20px 24px", border: "1px solid #e2e8f0" }}>
        <div style={{ fontSize: 12, color: "#64748b", textTransform: "uppercase", fontWeight: 600, letterSpacing: 0.4 }}>
          Presupuestos
        </div>
        <h3 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: "#0f172a" }}>Crear presupuesto</h3>
        <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>
          <i className="mdi mdi-domain me-1 text-primary" />
          <strong>{empresaNombre}</strong>
        </div>
      </div>

      <form onSubmit={handleSubmit} style={{ background: "#fff", borderRadius: 14, padding: 24, border: "1px solid #e2e8f0", display: "flex", flexDirection: "column", gap: 16 }}>

        {/* ─── Identificación ─── */}
        <section>
          <h6 style={sectionTitle}><i className="mdi mdi-card-account-details-outline me-2 text-primary" />Identificación</h6>
          <div style={grid}>
            <div style={{ gridColumn: "span 2" }}>
              <label style={labelStyle}>Nombre <span style={{ color: "#dc2626" }}>*</span></label>
              <input type="text" required value={form.nombre} onChange={e => set("nombre", e.target.value)}
                placeholder="Ej: Plan de ventas 2026" style={input} />
            </div>
            <div>
              <label style={labelStyle}>Alcance</label>
              <select value={form.sucursalId} onChange={e => set("sucursalId", e.target.value)} style={input} disabled={loadingSucursales}>
                <option value="">Toda la empresa</option>
                {sucursales.map(s => (
                  <option key={s.id || s.codigo} value={s.id}>
                    {s.nombre} {s.codigo && `(${s.codigo})`}
                  </option>
                ))}
              </select>
              {loadingSucursales && <small style={{ color: "#94a3b8" }}>Cargando sucursales…</small>}
            </div>
            <div style={{ gridColumn: "span 2" }}>
              <label style={labelStyle}>Descripción (opcional)</label>
              <input type="text" value={form.descripcion} onChange={e => set("descripcion", e.target.value)}
                placeholder="Breve resumen del objetivo del presupuesto" style={input} />
            </div>
          </div>
        </section>

        {/* ─── Periodo ─── */}
        <section>
          <h6 style={sectionTitle}><i className="mdi mdi-calendar-range me-2 text-primary" />Periodo y periodicidad</h6>
          <div style={grid}>
            <div>
              <label style={labelStyle}>Año <span style={{ color: "#dc2626" }}>*</span></label>
              <select value={form.anio} onChange={e => onAnioChange(Number(e.target.value))} style={input}>
                {ANIOS_DISPONIBLES.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Periodicidad <span style={{ color: "#dc2626" }}>*</span></label>
              <select value={form.periodicidad} onChange={e => set("periodicidad", e.target.value)} style={input}>
                {PERIODICIDADES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
              <small style={{ color: "#94a3b8", display: "block", marginTop: 3 }}>
                {PERIODICIDADES.find(p => p.value === form.periodicidad)?.descripcion}
              </small>
            </div>
            <div>
              <label style={labelStyle}>Fecha inicio <span style={{ color: "#dc2626" }}>*</span></label>
              <input type="date" value={toDateInput(form.fechaInicio)}
                onChange={e => set("fechaInicio", e.target.value)} style={input} />
            </div>
            <div>
              <label style={labelStyle}>Fecha fin <span style={{ color: "#dc2626" }}>*</span></label>
              <input type="date" value={toDateInput(form.fechaFin)} min={form.fechaInicio}
                onChange={e => set("fechaFin", e.target.value)} style={input} />
            </div>
          </div>
        </section>

        {/* ─── Medición y objetivo ─── */}
        <section>
          <h6 style={sectionTitle}><i className="mdi mdi-target me-2 text-primary" />Objetivo</h6>
          <div style={grid}>
            <div>
              <label style={labelStyle}>Tipo de medición</label>
              <select value={form.tipoMedicion} onChange={e => set("tipoMedicion", e.target.value)} style={input}>
                {TIPOS_MEDICION.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              <small style={{ color: "#94a3b8", display: "block", marginTop: 3 }}>
                {TIPOS_MEDICION.find(t => t.value === form.tipoMedicion)?.desc}
              </small>
            </div>
            <div>
              <label style={labelStyle}>Moneda</label>
              <select value={form.moneda} onChange={e => set("moneda", e.target.value)} style={input}>
                {monedaList.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            {(form.tipoMedicion === "MONTO" || form.tipoMedicion === "AMBOS") && (
              <div>
                <label style={labelStyle}>Monto total objetivo</label>
                <input type="number" step="0.01" min="0"
                  value={form.montoTotal} onChange={e => set("montoTotal", e.target.value)}
                  placeholder="Ej: 1200000000" style={input} />
                <small style={{ color: "#94a3b8", display: "block", marginTop: 3 }}>
                  Se distribuirá lineal entre los periodos. Podrás ajustar luego.
                </small>
              </div>
            )}
            {(form.tipoMedicion === "UNIDADES" || form.tipoMedicion === "AMBOS") && (
              <div>
                <label style={labelStyle}>Unidades total objetivo</label>
                <input type="number" step="1" min="0"
                  value={form.unidadesTotal} onChange={e => set("unidadesTotal", e.target.value)}
                  placeholder="Ej: 100000" style={input} />
              </div>
            )}
          </div>
        </section>

        {/* ─── Notas ─── */}
        <section>
          <h6 style={sectionTitle}><i className="mdi mdi-note-text-outline me-2 text-primary" />Notas (opcional)</h6>
          <textarea value={form.notas} onChange={e => set("notas", e.target.value)} rows={3}
            placeholder="Supuestos, racional, notas internas…"
            style={{ ...input, width: "100%", resize: "vertical", minHeight: 70 }} />
        </section>

        {/* ─── Validación / errores ─── */}
        {validacion.length > 0 && (
          <div style={{ background: "#fef3c7", border: "1px solid #fde68a", borderRadius: 8, padding: 12, color: "#92400e", fontSize: 13 }}>
            <strong><i className="mdi mdi-alert me-1" />Revisa estos campos:</strong>
            <ul style={{ margin: "6px 0 0 20px", padding: 0 }}>
              {validacion.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          </div>
        )}
        {error && (
          <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: 12, color: "#991b1b", fontSize: 13 }}>
            <i className="mdi mdi-alert-circle me-1" /> {error}
          </div>
        )}

        {/* ─── Acciones ─── */}
        <div className="d-flex justify-content-end gap-2">
          <button type="button" onClick={() => navigate("/portal/presupuestos")} className="btn btn-outline-secondary">
            Cancelar
          </button>
          <button type="submit" className="btn btn-primary" disabled={enviando}>
            {enviando ? <><Spinner size="sm" className="me-1" /> Creando…</> : (<><i className="mdi mdi-check me-1" /> Crear presupuesto</>)}
          </button>
        </div>
      </form>

      <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 10, padding: 14, fontSize: 12, color: "#1e40af" }}>
        <i className="mdi mdi-information-outline me-1" />
        El presupuesto se creará en estado <strong>BORRADOR</strong>. El back generará los periodos
        automáticamente y distribuirá el monto y las unidades en partes iguales. Después podrás
        ajustar manualmente desde el detalle.
      </div>
    </div>
  )
}

const sectionTitle = { fontSize: 13, fontWeight: 700, color: "#0f172a", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 10, paddingBottom: 6, borderBottom: "1px solid #e2e8f0" }
const grid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }
const labelStyle = { fontSize: 11, fontWeight: 600, color: "#475569", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4, display: "block" }
const input = { fontSize: 13, padding: "7px 10px", borderRadius: 6, border: "1px solid #cbd5e1", background: "#fff", width: "100%", height: 36 }

CrearPresupuesto.propTypes = {
  empresaSeleccionada: PropTypes.object,
  user: PropTypes.object,
  setBreadcrumbItems: PropTypes.func.isRequired,
}

const mapStateToProps = state => ({
  empresaSeleccionada: state.Login.empresaSeleccionada,
  user: state.Login.user,
})

export default connect(mapStateToProps, { setBreadcrumbItems })(CrearPresupuesto)
